import ExcelJS from 'exceljs';

import { ConfigReporte, FORMATO_EXCEL, calcularTotales } from './reportesConfig';

export interface ContextoReporte {
    sucursal: string;
    periodo: string;
    usuario: string;
    nombreArchivo: string;
    // Hojas que no aplican al filtro actual (p. ej. el detalle cuando se pide la gestión completa).
    hojasOmitidas: string[];
}

const FILA_CABECERA = 5;

const BORDE_FINO = {
    top:    { style: 'thin' as const },
    left:   { style: 'thin' as const },
    bottom: { style: 'thin' as const },
    right:  { style: 'thin' as const },
};

const dosDigitos = (n: number) => n.toString().padStart(2, '0');

const fechaHoraActual = () => {
    const ahora = new Date();
    return `${ dosDigitos(ahora.getDate()) }/${ dosDigitos(ahora.getMonth() + 1) }/${ ahora.getFullYear() }`
        + ` ${ dosDigitos(ahora.getHours()) }:${ dosDigitos(ahora.getMinutes()) }`;
};

const escribirEncabezado = (hoja: ExcelJS.Worksheet, totalColumnas: number, lineas: { texto: string, font: Partial<ExcelJS.Font> }[]) => {
    lineas.forEach((linea, index) => {
        const fila = index + 1;
        hoja.mergeCells(fila, 1, fila, totalColumnas);
        const celda = hoja.getCell(fila, 1);
        celda.value = linea.texto;
        celda.font = linea.font;
        celda.alignment = { horizontal: 'center', vertical: 'middle' };
    });
    hoja.getRow(1).height = 24;
};

export const exportarReporteExcel = async (
    config: ConfigReporte,
    datos: Record<string, any[]>,
    contexto: ContextoReporte,
) => {
    const workbook = new ExcelJS.Workbook();
    workbook.creator = 'MarketBo';
    workbook.created = new Date();

    config.hojas
        .filter((definicion) => !contexto.hojasOmitidas.includes(definicion.key))
        .forEach((definicion) => {
            const filas = datos[definicion.key] || [];
            const columnas = definicion.columnas;

            const hoja = workbook.addWorksheet(definicion.nombre.substring(0, 31), {
                views: [{ state: 'frozen', ySplit: FILA_CABECERA }],
            });
            hoja.columns = columnas.map(({ key, width }) => ({ key, width }));

            escribirEncabezado(hoja, columnas.length, [
                { texto: `${ config.titulo } - ${ definicion.nombre }`, font: { bold: true, size: 14 } },
                { texto: `Sucursal: ${ contexto.sucursal }    |    Período: ${ contexto.periodo }`, font: { bold: true, size: 11 } },
                { texto: `Generado por: ${ contexto.usuario }    |    Fecha y hora del reporte: ${ fechaHoraActual() }`, font: { italic: true, size: 10 } },
            ]);

            const filaCabecera = hoja.getRow(FILA_CABECERA);
            columnas.forEach((columna, index) => {
                const celda = filaCabecera.getCell(index + 1);
                celda.value = columna.header;
                celda.font = { bold: true, color: { argb: 'FFFFFFFF' } };
                celda.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF2F5597' } };
                celda.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
                celda.border = BORDE_FINO;
            });

            filas.forEach((item) => {
                const fila = hoja.addRow(item);
                columnas.forEach((columna) => {
                    const celda = fila.getCell(columna.key);
                    celda.border = BORDE_FINO;
                    const formato = FORMATO_EXCEL[columna.tipo];
                    if (formato) celda.numFmt = formato;
                });
            });

            if (filas.length) {
                hoja.autoFilter = {
                    from: { row: FILA_CABECERA, column: 1 },
                    to:   { row: FILA_CABECERA + filas.length, column: columnas.length },
                };
            }

            // Fila de totales
            if (columnas.some((c) => c.total)) {
                const totales = calcularTotales(definicion, filas);
                const filaTotal = hoja.addRow({});
                columnas.forEach((columna, index) => {
                    const celda = filaTotal.getCell(index + 1);
                    if (index === 0) celda.value = 'TOTAL';
                    if (columna.total) celda.value = totales[columna.key];
                    const formato = FORMATO_EXCEL[columna.tipo];
                    if (formato && columna.total) celda.numFmt = formato;
                    celda.font = { bold: true };
                    celda.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD9E1F2' } };
                    celda.border = BORDE_FINO;
                });
            }
        });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    const enlace = document.createElement('a');
    enlace.href = URL.createObjectURL(blob);
    enlace.download = contexto.nombreArchivo;
    document.body.appendChild(enlace);
    enlace.click();
    document.body.removeChild(enlace);
    URL.revokeObjectURL(enlace.href);
};
